/**
 * LandingCollagenType3Widget — "Denser Breast Quest" (iter724, Feb 2026)
 *
 * A 3-phase gamified explainer for **Type-III collagen synthesis**:
 *
 *   PHASE 1 (LEARN) — brief animated card explaining WHY Type-III collagen
 *     matters for breast tissue density (loose stromal network vs. rigid
 *     Type-I crosslinks that raise mammographic density and cancer risk).
 *
 *   PHASE 2 (BUILD) — cofactor puzzle: user clicks 5 tiles
 *     (Vit C, glycine, proline, Zn, Cu) → progress bar fills → the
 *     triple-helix visual assembles strand-by-strand.
 *
 *   PHASE 3 (BALANCE) — Type-I vs Type-III slider. User drags a slider that
 *     tilts a balance scale. Real-time readout of the ratio + healthy zone
 *     highlight (~1 : 2 Type-III : Type-I).
 *
 * Score = 100 × completion (build_pct × 0.5  +  balance_zone_hit × 0.5).
 * On score ≥ 80 → confetti + CTA to the full Plate Simulator.
 *
 * Bulgarian primary language + English fallback (via LanguageContext).
 * Match landing-page dark/light theming (via ThemeContext).
 * No emojis — lucide-react icons only (per house style).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import {
  Sparkles, ArrowRight, Check, Beaker, Scale as ScaleIcon,
  Zap, RotateCcw, ChevronRight, Award, BookOpen, HelpCircle,
  Utensils, Search, Plus, X as XIcon,
} from "lucide-react";

import { COL3_FOODS_BG, COL3_SUBSTITUTIONS } from "./col3-foods-db";

// ─────────────────────────────────────────────────────────────────────
// Cofactor data — grounded in the same peer-reviewed set used by the
// Plate Simulator COL3 score (see routes/foodlab.py line ~940):
//    Vit C   — proline hydroxylation (rate-limiting)
//    Glycine — every 3rd residue of the α-chain
//    Proline — hydroxylated to hydroxyproline (triple-helix stability)
//    Zn      — matrix metalloproteinase remodelling
//    Cu      — lysyl oxidase crosslinking
// ─────────────────────────────────────────────────────────────────────
const COFACTORS = [
  {
    key: "vit_c",
    color: "amber",
    label_bg: "Витамин C",
    label_en: "Vitamin C",
    role_bg: "Ко-ензим за пролил-хидроксилазата — задължителен за стабилността на тройната спирала.",
    role_en: "Cofactor for prolyl-hydroxylase — required for triple-helix stability.",
    top_foods_bg: "Шипка · Ацерола · Червен пипер · Киви",
    top_foods_en: "Rose-hip · Acerola · Bell pepper · Kiwi",
    icon: "C",
  },
  {
    key: "glycine",
    color: "emerald",
    label_bg: "Глицин",
    label_en: "Glycine",
    role_bg: "Всяка 3-та аминокиселина в COL3 α1-веригата. Без глицин – няма фибрил.",
    role_en: "Every 3rd amino acid in the COL3 α1 chain. No glycine → no fibril.",
    top_foods_bg: "Костен бульон · Желатин · Пилешка кожа · Свинско джолан",
    top_foods_en: "Bone broth · Gelatin · Chicken skin · Pork hock",
    icon: "G",
  },
  {
    key: "proline",
    color: "sky",
    label_bg: "Пролин",
    label_en: "Proline",
    role_bg: "Хидроксилиран до хидроксипролин — enforces triple-helix folding.",
    role_en: "Hydroxylated to hydroxyproline — enforces triple-helix folding.",
    top_foods_bg: "Костен бульон · Извара · Яйчен белтък · Сирене",
    top_foods_en: "Bone broth · Cottage cheese · Egg white · Cheese",
    icon: "P",
  },
  {
    key: "zinc",
    color: "purple",
    label_bg: "Цинк (Zn)",
    label_en: "Zinc (Zn)",
    role_bg: "Активира матриксни металопротеинази за здравословно ремоделиране.",
    role_en: "Activates matrix metalloproteinases for healthy remodelling.",
    top_foods_bg: "Стриди · Черен дроб · Тиквени семки · Кашу",
    top_foods_en: "Oysters · Beef liver · Pumpkin seeds · Cashews",
    icon: "Zn",
  },
  {
    key: "copper",
    color: "orange",
    label_bg: "Мед (Cu)",
    label_en: "Copper (Cu)",
    role_bg: "Кофактор на lysyl oxidase — сшива нишките без свръхплътност.",
    role_en: "Cofactor of lysyl oxidase — crosslinks strands without over-density.",
    top_foods_bg: "Черен дроб · Стриди · Гъби шитаке · Тъмен шоколад",
    top_foods_en: "Liver · Oysters · Shiitake · Dark chocolate",
    icon: "Cu",
  },
];

// Color → Tailwind class map (kept explicit so Tailwind doesn't purge them)
const CLR = {
  amber:   { bg: "bg-amber-500",  ring: "ring-amber-500",   text: "text-amber-100",  soft: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  emerald: { bg: "bg-emerald-500", ring: "ring-emerald-500", text: "text-emerald-100", soft: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  sky:     { bg: "bg-sky-500",     ring: "ring-sky-500",     text: "text-sky-100",     soft: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
  purple:  { bg: "bg-purple-500",  ring: "ring-purple-500",  text: "text-purple-100",  soft: "bg-purple-500/15 text-purple-300 border-purple-500/40" },
  orange:  { bg: "bg-orange-500",  ring: "ring-orange-500",  text: "text-orange-100",  soft: "bg-orange-500/15 text-orange-300 border-orange-500/40" },
};

// Healthy Type-III : Type-I ratio zone (dense-breast literature).
// Below the zone → too little COL3, tissue rigidises → higher density.
// Above the zone → too much COL3, tissue loses tensile support.
// Sweet spot ≈ 30 - 45 % Type-III (with 55 - 70 % Type-I).
const IDEAL_MIN_PCT = 30;
const IDEAL_MAX_PCT = 45;


export default function LandingCollagenType3Widget() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  // ─────────── State ───────────
  const [phase, setPhase] = useState(1);                 // 1..4
  const [builtSet, setBuiltSet] = useState(new Set());    // Phase 2 selections
  const [hoveredCofactor, setHoveredCofactor] = useState(null);
  const [type3Pct, setType3Pct] = useState(20);           // Phase 3 slider (starts sub-optimal)
  const [pickedFoods, setPickedFoods] = useState([null, null, null]);  // Phase 4 — up to 3 foods
  const [showComplete, setShowComplete] = useState(false);
  const [confetti, setConfetti] = useState(false);
  const scoreRef = useRef(null);
  const autoAdvancedOnce = useRef(false);  // fire the P2→P3 jump only once

  // ─────────── Derived ───────────
  const buildPct = Math.round((builtSet.size / COFACTORS.length) * 100);
  const inHealthyZone = type3Pct >= IDEAL_MIN_PCT && type3Pct <= IDEAL_MAX_PCT;
  // Scanner score: average per-food COL3 score across the 3 slots (empty=0)
  const filledFoods = pickedFoods.filter(Boolean);
  const scannerAvg = filledFoods.length === 0
    ? 0
    : Math.round(filledFoods.reduce((s, f) => s + f.score, 0) / filledFoods.length);
  // Weights: 35% cofactors + 35% balance + 30% real-food scanner
  const score = Math.round(
    buildPct * 0.35 + (inHealthyZone ? 35 : 0) + scannerAvg * 0.30
  );

  // Handlers
  const pickCofactor = (key) => {
    setBuiltSet(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const reset = () => {
    setBuiltSet(new Set());
    setType3Pct(20);
    setPickedFoods([null, null, null]);
    setPhase(1);
    setShowComplete(false);
    setConfetti(false);
    autoAdvancedOnce.current = false;
  };

  // Trigger completion flash + confetti-ish pulse when all 3 phases done well
  useEffect(() => {
    if ((phase === 3 || phase === 4) && builtSet.size === COFACTORS.length && inHealthyZone && scannerAvg >= 40) {
      if (!showComplete) {
        setShowComplete(true);
        setConfetti(true);
        // Auto-scroll the CTA into view for touch users
        setTimeout(() => scoreRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 200);
        setTimeout(() => setConfetti(false), 2200);
      }
    } else if (showComplete) {
      setShowComplete(false);
    }
  }, [phase, builtSet.size, inHealthyZone, scannerAvg, showComplete]);

  // Auto-advance from Phase 2 → 3 when all cofactors collected — ONLY ONCE
  // per session, so navigating back to Phase 2 later doesn't yank the user.
  useEffect(() => {
    if (phase === 2 && builtSet.size === COFACTORS.length && !autoAdvancedOnce.current) {
      autoAdvancedOnce.current = true;
      const t = setTimeout(() => setPhase(3), 1400);
      return () => clearTimeout(t);
    }
  }, [phase, builtSet.size]);

  // ─────────── Render ───────────
  return (
    <section
      data-testid="landing-col3-widget"
      className={`relative overflow-hidden py-16 px-4 sm:px-6 lg:px-8 ${
        isDark ? "bg-gradient-to-b from-slate-950 via-purple-950/30 to-slate-950" : "bg-gradient-to-b from-white via-purple-50/40 to-white"
      }`}
    >
      {/* Ambient grain / noise pattern */}
      <div className={`absolute inset-0 pointer-events-none opacity-40 ${
        isDark ? "bg-[radial-gradient(circle_at_20%_10%,rgba(168,85,247,0.15),transparent_40%),radial-gradient(circle_at_80%_60%,rgba(59,130,246,0.15),transparent_50%)]" : "bg-[radial-gradient(circle_at_20%_10%,rgba(168,85,247,0.10),transparent_40%),radial-gradient(circle_at_80%_60%,rgba(59,130,246,0.08),transparent_50%)]"
      }`} />

      <div className="max-w-6xl mx-auto relative">
        {/* ─────────── Section header ─────────── */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 mb-8">
          <div>
            <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
              isDark ? "bg-purple-500/15 text-purple-300 border border-purple-500/30" : "bg-purple-100 text-purple-700 border border-purple-300"
            }`}>
              <Beaker className="w-3 h-3" />
              {bg ? "Интерактивно · за плътна гърда" : "Interactive · dense-breast science"}
            </div>
            <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-black mt-3 tracking-tight ${
              isDark ? "text-white" : "text-slate-900"
            }`}>
              {bg ? "Синтезирай своя " : "Synthesise your "}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-amber-400">
                {bg ? "Колаген Тип 3" : "Type-III Collagen"}
              </span>
            </h2>
            <p className={`mt-2 text-sm sm:text-base max-w-2xl ${
              isDark ? "text-slate-300" : "text-slate-600"
            }`}>
              {bg
                ? "Колаген Тип 3 = гъвкавата стромална мрежа на гърдата. Твърде малко → тъканта се уплътнява (COL1↑ crosslinks) → мамографска плътност и риск. Изгради баланса за 3 фази."
                : "Type-III collagen = the flexible stromal network of breast tissue. Too little → the tissue rigidises (COL1↑ crosslinks) → mammographic density & risk. Build the balance in 3 phases."}
            </p>
          </div>

          {/* Phase indicator */}
          <div className="flex flex-wrap items-center gap-1.5">
            {[1, 2, 3, 4].map(p => (
              <button
                key={p}
                onClick={() => setPhase(p)}
                data-testid={`col3-phase-${p}`}
                className={`h-8 px-3 rounded-full text-[11px] font-bold transition-all ${
                  phase === p
                    ? "bg-purple-500 text-white shadow-lg shadow-purple-500/30 scale-105"
                    : (isDark ? "bg-white/5 text-slate-400 hover:bg-white/10" : "bg-slate-100 text-slate-500 hover:bg-slate-200")
                }`}
              >
                {p === 1 ? (bg ? "1 · Учи" : "1 · Learn")
                 : p === 2 ? (bg ? "2 · Изгради" : "2 · Build")
                 : p === 3 ? (bg ? "3 · Балансирай" : "3 · Balance")
                 : (bg ? "4 · Сканирай" : "4 · Scan")}
              </button>
            ))}
          </div>
        </div>

        {/* ═══════════════════ PHASE 1 — LEARN ═══════════════════ */}
        {phase === 1 && (
          <PhaseLearn bg={bg} isDark={isDark} onNext={() => setPhase(2)} />
        )}

        {/* ═══════════════════ PHASE 2 — BUILD ═══════════════════ */}
        {phase === 2 && (
          <PhaseBuild
            bg={bg} isDark={isDark}
            cofactors={COFACTORS} builtSet={builtSet} buildPct={buildPct}
            hoveredCofactor={hoveredCofactor}
            onHover={setHoveredCofactor} onPick={pickCofactor}
            onNext={() => setPhase(3)}
          />
        )}

        {/* ═══════════════════ PHASE 3 — BALANCE ═══════════════════ */}
        {phase === 3 && (
          <PhaseBalance
            bg={bg} isDark={isDark}
            type3Pct={type3Pct} onChange={setType3Pct}
            inHealthyZone={inHealthyZone}
            idealMin={IDEAL_MIN_PCT} idealMax={IDEAL_MAX_PCT}
            onNext={() => setPhase(4)}
          />
        )}

        {/* ═══════════════════ PHASE 4 — SCAN (real foods) ═══════════════════ */}
        {phase === 4 && (
          <PhaseScan
            bg={bg} isDark={isDark}
            foods={pickedFoods} onChangeFoods={setPickedFoods}
          />
        )}

        {/* ═══════════════════ SCORE + CTA (visible always after phase 2) ═══════════════════ */}
        {phase >= 2 && (
          <div
            ref={scoreRef}
            className={`mt-8 p-6 rounded-2xl border-2 relative overflow-hidden ${
              showComplete
                ? "border-emerald-400 bg-gradient-to-r from-emerald-500/10 via-purple-500/10 to-amber-500/10"
                : (isDark ? "border-white/10 bg-white/5" : "border-slate-200 bg-white")
            }`}
            data-testid="col3-score-card"
          >
            {confetti && <ConfettiBurst />}

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className={`h-16 w-16 flex-shrink-0 rounded-2xl flex items-center justify-center ${
                  score >= 80 ? "bg-emerald-500"
                  : score >= 50 ? "bg-amber-500"
                  : "bg-slate-500"
                }`}>
                  <div className="text-center text-white">
                    <div className="text-lg font-black leading-none">{score}</div>
                    <div className="text-[8px] font-semibold opacity-80">/ 100</div>
                  </div>
                </div>
                <div>
                  <div className={`text-xs font-bold uppercase tracking-wider ${
                    isDark ? "text-slate-400" : "text-slate-500"
                  }`}>
                    {bg ? "Твоят COL3 Score" : "Your COL3 Score"}
                  </div>
                  <div className={`text-lg sm:text-xl font-bold ${
                    isDark ? "text-white" : "text-slate-900"
                  }`}>
                    {score >= 80
                      ? (bg ? "Отличен баланс! Триплоидна спирала е стабилна." : "Excellent balance! Triple helix is stable.")
                      : score >= 50
                      ? (bg ? "Добро начало — довърши всички кофактори + баланс." : "Good start — complete all cofactors + balance.")
                      : (bg ? "Продължи през 3-те фази за пълен резултат." : "Continue through all 3 phases for full score.")}
                  </div>
                  <div className={`text-[11px] mt-0.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    {bg
                      ? `Кофактори: ${builtSet.size}/5 · Баланс: ${inHealthyZone ? "оптимален" : "извън зона"} · Скенер: ${filledFoods.length}/3 (avg ${scannerAvg})`
                      : `Cofactors: ${builtSet.size}/5 · Balance: ${inHealthyZone ? "optimal" : "off-zone"} · Scanner: ${filledFoods.length}/3 (avg ${scannerAvg})`}
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                <Button
                  variant="outline" size="sm"
                  onClick={reset}
                  data-testid="col3-reset"
                  className={isDark ? "border-white/20 text-slate-300 hover:bg-white/10" : "border-slate-300 text-slate-700"}
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                  {bg ? "Изчисти" : "Reset"}
                </Button>
                <Link to="/smart/plate-simulator" data-testid="col3-cta-simulator">
                  <Button
                    className="w-full sm:w-auto gradient-primary text-white font-bold shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50"
                  >
                    <Sparkles className="w-4 h-4 mr-1.5" />
                    {bg ? "Виж пълния план в Симулатора" : "See full plan in Plate Simulator"}
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}


// ─────────────────────────────────────────────────────────────────────
// PHASE 1 — LEARN
// ─────────────────────────────────────────────────────────────────────
function PhaseLearn({ bg, isDark, onNext }) {
  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Left column — narrative */}
      <div className={`p-6 rounded-2xl border ${
        isDark ? "border-white/10 bg-white/5" : "border-slate-200 bg-white shadow-lg"
      }`}>
        <div className="flex items-center gap-2 mb-3">
          <BookOpen className={`w-5 h-5 ${isDark ? "text-purple-300" : "text-purple-600"}`} />
          <h3 className={`text-lg font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
            {bg ? "Защо COL3 е важен за плътната гърда?" : "Why COL3 matters for dense breast"}
          </h3>
        </div>
        <div className={`space-y-2.5 text-sm leading-relaxed ${
          isDark ? "text-slate-300" : "text-slate-700"
        }`}>
          <p>
            <strong className="text-purple-400">
              {bg ? "Колаген Тип 3" : "Type-III collagen"}
            </strong>{" "}
            {bg
              ? "формира тънки, еластични фибрили — стромалната „мека тъкан“ около млечните канали. Като амортисьор."
              : "forms thin elastic fibrils — the stromal 'soft mesh' around milk ducts. It cushions."}
          </p>
          <p>
            <strong className={isDark ? "text-amber-300" : "text-amber-600"}>
              {bg ? "Колаген Тип 1" : "Type-I collagen"}
            </strong>{" "}
            {bg
              ? "е дебелият структурен фибрил. При хронично възпаление ензимът LOX го crosslink-ва — тъканта се втвърдява, мамографската плътност се вдига."
              : "is the thick structural fibril. Under chronic inflammation LOX crosslinks it — tissue stiffens, mammographic density rises."}
          </p>
          <p>
            <strong className={isDark ? "text-emerald-300" : "text-emerald-600"}>
              {bg ? "Здравословният баланс" : "The healthy balance"}
            </strong>
            {bg ? " ≈ 30–45% COL3 в общия колаген. " : " ≈ 30–45% COL3 in total collagen. "}
            {bg
              ? "Твърде малко COL3 → тъканта губи еластичност; твърде много → тъканта губи структура."
              : "Too little COL3 → tissue loses elasticity; too much → tissue loses structure."}
          </p>
        </div>

        <div className={`mt-4 p-3 rounded-lg border text-[11px] ${
          isDark ? "border-purple-500/30 bg-purple-500/10 text-purple-200"
                 : "border-purple-300 bg-purple-50 text-purple-800"
        }`}>
          <HelpCircle className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
          {bg
            ? "Референции: Provenzano et al., BMC Med 2008 · Boyd et al., NEJM 2007 · Levental et al., Cell 2009."
            : "References: Provenzano et al., BMC Med 2008 · Boyd et al., NEJM 2007 · Levental et al., Cell 2009."}
        </div>
      </div>

      {/* Right column — visual */}
      <div className={`p-6 rounded-2xl border relative overflow-hidden ${
        isDark ? "border-white/10 bg-gradient-to-br from-purple-950/40 to-slate-950" : "border-slate-200 bg-gradient-to-br from-purple-50 to-white"
      }`}>
        <BreastTissueSVG isDark={isDark} />
        <p className={`mt-3 text-[11px] italic ${isDark ? "text-slate-400" : "text-slate-500"}`}>
          {bg
            ? "Ляво: здрава тъкан (COL3 еластичен слой). Дясно: втвърдена, плътна тъкан (COL1↑ crosslinks)."
            : "Left: healthy tissue (COL3 elastic layer). Right: rigid dense tissue (COL1↑ crosslinks)."}
        </p>
      </div>

      <div className="md:col-span-2 flex justify-end">
        <Button
          onClick={onNext}
          data-testid="col3-phase1-next"
          className="gradient-primary text-white font-bold"
        >
          {bg ? "Изгради кофакторите" : "Build the cofactors"}
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// PHASE 2 — BUILD  (cofactor puzzle)
// ─────────────────────────────────────────────────────────────────────
function PhaseBuild({ bg, isDark, cofactors, builtSet, buildPct, hoveredCofactor, onHover, onPick, onNext }) {
  const hovered = cofactors.find(c => c.key === hoveredCofactor);
  return (
    <div className="grid md:grid-cols-3 gap-6">
      {/* Cofactor tiles */}
      <div className="md:col-span-2 space-y-3">
        <div className={`p-4 rounded-xl border flex items-center justify-between ${
          isDark ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
        }`}>
          <div>
            <div className={`text-xs font-bold uppercase tracking-wider ${isDark ? "text-purple-300" : "text-purple-700"}`}>
              {bg ? "Прогрес" : "Progress"}
            </div>
            <div className={`text-lg font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              {builtSet.size}/{cofactors.length} {bg ? "кофактора" : "cofactors"}
            </div>
          </div>
          <div className="w-32 sm:w-48 h-3 rounded-full overflow-hidden bg-slate-700/40">
            <div
              className="h-full bg-gradient-to-r from-purple-500 via-pink-500 to-amber-400 transition-all duration-500"
              style={{ width: `${buildPct}%` }}
              data-testid="col3-build-progress"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {cofactors.map(c => {
            const active = builtSet.has(c.key);
            const clr = CLR[c.color];
            return (
              <button
                key={c.key}
                onClick={() => onPick(c.key)}
                onMouseEnter={() => onHover(c.key)}
                onMouseLeave={() => onHover(null)}
                data-testid={`col3-cofactor-${c.key}`}
                className={`relative aspect-square rounded-2xl border-2 transition-all duration-300 flex flex-col items-center justify-center ${
                  active
                    ? `${clr.bg} border-transparent scale-105 shadow-lg shadow-black/30`
                    : (isDark ? "border-white/10 bg-white/5 hover:border-white/30" : "border-slate-200 bg-white hover:border-purple-400")
                }`}
              >
                {active && (
                  <Check className="absolute top-1.5 right-1.5 w-3.5 h-3.5 text-white" />
                )}
                <div className={`text-xl sm:text-2xl font-black ${active ? "text-white" : (isDark ? "text-slate-300" : "text-slate-700")}`}>
                  {c.icon}
                </div>
                <div className={`text-[10px] sm:text-[11px] font-semibold text-center mt-1 px-1 ${active ? "text-white" : (isDark ? "text-slate-400" : "text-slate-600")}`}>
                  {bg ? c.label_bg : c.label_en}
                </div>
              </button>
            );
          })}
        </div>

        <p className={`text-[11px] mt-2 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
          {bg
            ? "Кликни всеки кофактор, за да го добавиш към спиралата. Задръж мишката за клинична роля + топ храни."
            : "Click each cofactor to add it to the helix. Hover for clinical role + top foods."}
        </p>
      </div>

      {/* Right side — triple-helix visual + hovered detail */}
      <div className={`p-4 rounded-2xl border relative flex flex-col ${
        isDark ? "border-white/10 bg-gradient-to-br from-slate-900 to-purple-950/40" : "border-slate-200 bg-gradient-to-br from-purple-50 to-white"
      }`}>
        <TripleHelixVisual builtCount={builtSet.size} total={cofactors.length} isDark={isDark} />

        {hovered ? (
          <div className={`mt-3 p-3 rounded-lg border text-[11px] ${CLR[hovered.color].soft}`}>
            <div className="font-bold mb-1">{bg ? hovered.label_bg : hovered.label_en}</div>
            <div className="mb-1.5 opacity-90">{bg ? hovered.role_bg : hovered.role_en}</div>
            <div className="opacity-75">
              <strong>{bg ? "Топ храни:" : "Top foods:"}</strong> {bg ? hovered.top_foods_bg : hovered.top_foods_en}
            </div>
          </div>
        ) : (
          <div className={`mt-3 p-3 rounded-lg border border-dashed text-[11px] italic text-center ${
            isDark ? "border-white/10 text-slate-500" : "border-slate-300 text-slate-400"
          }`}>
            {bg ? "Задръж мишката върху кофактор за детайли" : "Hover a cofactor for details"}
          </div>
        )}
      </div>

      {buildPct === 100 && (
        <div className="md:col-span-3 flex justify-end">
          <Button onClick={onNext} data-testid="col3-phase2-next" className="gradient-primary text-white font-bold">
            {bg ? "Балансирай тип 1 и тип 3" : "Balance Type 1 and Type 3"}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// PHASE 3 — BALANCE  (Type-I vs Type-III scale)
// ─────────────────────────────────────────────────────────────────────
function PhaseBalance({ bg, isDark, type3Pct, onChange, inHealthyZone, idealMin, idealMax, onNext }) {
  const type1Pct = 100 - type3Pct;
  // Scale tilt: -18° when only COL1, +18° when only COL3, 0° at 50/50.
  const tilt = ((type3Pct - 50) / 50) * 18;

  return (
    <div className="grid md:grid-cols-3 gap-6">
      {/* Scale visual */}
      <div className={`md:col-span-2 p-6 rounded-2xl border relative min-h-[340px] ${
        isDark ? "border-white/10 bg-gradient-to-br from-slate-900 to-purple-950/30" : "border-slate-200 bg-gradient-to-br from-purple-50 to-white shadow-lg"
      }`}>
        <BalanceScale tilt={tilt} type1Pct={type1Pct} type3Pct={type3Pct} inHealthyZone={inHealthyZone} isDark={isDark} bg={bg} />

        {/* Slider */}
        <div className="mt-4 px-2">
          <div className="flex justify-between text-[10px] font-bold uppercase mb-1">
            <span className={isDark ? "text-amber-300" : "text-amber-700"}>
              {bg ? "Тип 1 (структура)" : "Type 1 (structure)"}
            </span>
            <span className={isDark ? "text-purple-300" : "text-purple-700"}>
              {bg ? "Тип 3 (гъвкавост)" : "Type 3 (flexibility)"}
            </span>
          </div>
          <div className="relative">
            {/* Healthy zone highlight track */}
            <div
              className="absolute top-1/2 -translate-y-1/2 h-2 rounded-full bg-emerald-500/40 pointer-events-none"
              style={{ left: `${idealMin}%`, width: `${idealMax - idealMin}%` }}
            />
            <input
              type="range" min="5" max="80" step="1"
              value={type3Pct}
              onChange={(e) => onChange(Number(e.target.value))}
              data-testid="col3-balance-slider"
              className="w-full h-2 rounded-full appearance-none cursor-pointer accent-purple-500 bg-gradient-to-r from-amber-500/40 via-slate-500/40 to-purple-500/40"
            />
          </div>
          <div className="flex justify-between mt-2 text-[11px]">
            <span className={isDark ? "text-amber-300" : "text-amber-700"}>{type1Pct}%</span>
            <span className={`font-bold ${inHealthyZone ? (isDark ? "text-emerald-300" : "text-emerald-600") : (isDark ? "text-slate-500" : "text-slate-400")}`}>
              {bg ? `Здрава зона ${idealMin}–${idealMax}%` : `Healthy zone ${idealMin}–${idealMax}%`}
            </span>
            <span className={isDark ? "text-purple-300" : "text-purple-700"}>{type3Pct}%</span>
          </div>
        </div>
      </div>

      {/* Right side — verdict card */}
      <div className={`p-5 rounded-2xl border flex flex-col ${
        isDark ? "border-white/10 bg-white/5" : "border-slate-200 bg-white shadow-lg"
      }`}>
        <div className="flex items-center gap-2 mb-2">
          <ScaleIcon className={`w-4 h-4 ${inHealthyZone ? "text-emerald-500" : "text-amber-500"}`} />
          <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
            {bg ? "Диагноза" : "Verdict"}
          </h3>
        </div>

        <div className={`p-3 rounded-lg text-xs leading-relaxed ${
          inHealthyZone
            ? (isDark ? "bg-emerald-500/15 text-emerald-100 border border-emerald-500/30" : "bg-emerald-50 text-emerald-900 border border-emerald-300")
            : (isDark ? "bg-amber-500/15 text-amber-100 border border-amber-500/30" : "bg-amber-50 text-amber-900 border border-amber-300")
        }`}>
          {inHealthyZone ? (
            bg
              ? "Гърдовата строма запазва еластичност. Мамографската плътност остава ниска и колагеновата crosslinking е контролирана."
              : "Breast stroma stays elastic. Mammographic density remains low and collagen crosslinking is controlled."
          ) : type3Pct < idealMin ? (
            bg
              ? "Твърде малко COL3. При хронично възпаление LOX ще crosslink-не COL1 → втвърдяване и повишена плътност."
              : "Too little COL3. Under chronic inflammation LOX will crosslink COL1 → stiffening and higher density."
          ) : (
            bg
              ? "Твърде много COL3 за структурата. Загуба на tensile support — риск от лактационни и постменопаузни промени."
              : "Too much COL3 for the structure. Loss of tensile support — risk of lactational and post-menopausal remodelling."
          )}
        </div>

        <div className={`mt-3 text-[11px] space-y-1 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
          <div className="flex items-center gap-1.5">
            <Zap className="w-3 h-3 text-purple-400" />
            <span>
              {bg ? "COL3-boost храни: шипка, стриди, костен бульон" : "COL3-boost foods: rose-hip, oysters, bone broth"}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Zap className="w-3 h-3 text-amber-400" />
            <span>
              {bg ? "COL1-контрол: намали LOX активатори (hsCRP↓)" : "COL1-control: reduce LOX activators (hsCRP↓)"}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Award className="w-3 h-3 text-emerald-400" />
            <span>
              {bg ? "Плодотворна цел ≈ 35% COL3" : "Sweet spot ≈ 35% COL3"}
            </span>
          </div>
        </div>

        <div className="mt-4">
          <Button
            onClick={onNext}
            data-testid="col3-phase3-next"
            className="w-full gradient-primary text-white font-bold"
          >
            {bg ? "Сканирай реалната закуска" : "Scan a real breakfast"}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// PHASE 4 — SCAN  (real foods → live COL3 verdict + substitution suggestions)
// ─────────────────────────────────────────────────────────────────────
function PhaseScan({ bg, isDark, foods, onChangeFoods }) {
  const filled = foods.filter(Boolean);
  const avg = filled.length === 0
    ? 0
    : Math.round(filled.reduce((s, f) => s + f.score, 0) / filled.length);
  const hasAntagonist = filled.some(f => f.tags.includes("antagonist"));

  // Detect which cofactor buckets are covered vs. missing (for suggestions)
  const covered = new Set();
  filled.forEach(f => f.tags.forEach(t => covered.add(t)));
  const buckets = ["vit_c", "glycine", "proline", "zinc", "copper"];
  const missing = buckets.filter(b => !covered.has(b));

  // Build substitution recommendations: for each missing bucket up to 2 foods
  const suggestions = missing.slice(0, 2).flatMap(bucket =>
    (COL3_SUBSTITUTIONS[bucket] || [])
      .map(key => COL3_FOODS_BG.find(f => f.key === key))
      .filter(Boolean)
      .slice(0, 1)  // 1 per missing bucket → max 2 total
      .map(f => ({ ...f, missing_bucket: bucket }))
  );

  const setSlot = (idx, food) => {
    const next = [...foods];
    next[idx] = food;
    onChangeFoods(next);
  };

  const bucketLabel = (b) => {
    const m = {
      vit_c:   bg ? "Витамин C"  : "Vitamin C",
      glycine: bg ? "Глицин"     : "Glycine",
      proline: bg ? "Пролин"     : "Proline",
      zinc:    bg ? "Цинк"       : "Zinc",
      copper:  bg ? "Мед"        : "Copper",
    };
    return m[b] || b;
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Left — 3 food slots */}
      <div className={`p-5 rounded-2xl border ${
        isDark ? "border-white/10 bg-white/5" : "border-slate-200 bg-white shadow-lg"
      }`}>
        <div className="flex items-center gap-2 mb-3">
          <Utensils className={`w-4 h-4 ${isDark ? "text-purple-300" : "text-purple-600"}`} />
          <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
            {bg ? "Твоята закуска (до 3 храни)" : "Your breakfast (up to 3 foods)"}
          </h3>
        </div>

        <div className="space-y-2">
          {[0, 1, 2].map(idx => (
            <FoodSlot
              key={idx} idx={idx} food={foods[idx]}
              onPick={(f) => setSlot(idx, f)}
              onClear={() => setSlot(idx, null)}
              bg={bg} isDark={isDark}
            />
          ))}
        </div>

        <p className={`text-[10px] mt-3 italic ${isDark ? "text-slate-500" : "text-slate-500"}`}>
          {bg
            ? "Списъкът обхваща 45 често срещани храни (в куратираната Nutrami база са 3000+)."
            : "This list has 45 common foods (curated Nutrami DB has 3000+)."}
        </p>
      </div>

      {/* Right — verdict + suggestions */}
      <div className={`p-5 rounded-2xl border flex flex-col ${
        isDark ? "border-white/10 bg-gradient-to-br from-slate-900 to-purple-950/40" : "border-slate-200 bg-gradient-to-br from-purple-50 to-white shadow-lg"
      }`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ScaleIcon className={`w-4 h-4 ${
              avg >= 60 ? "text-emerald-500" : avg >= 30 ? "text-amber-500" : "text-red-500"
            }`} />
            <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              {bg ? "COL3 диагноза на комбинацията" : "COL3 verdict of combo"}
            </h3>
          </div>
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center text-white font-black text-sm ${
            avg >= 60 ? "bg-emerald-500" : avg >= 30 ? "bg-amber-500" : "bg-slate-500"
          }`}
            data-testid="col3-scanner-avg">
            {avg}
          </div>
        </div>

        {filled.length === 0 ? (
          <div className={`p-4 rounded-lg border border-dashed text-xs italic text-center ${
            isDark ? "border-white/10 text-slate-500" : "border-slate-300 text-slate-400"
          }`}>
            {bg
              ? "Избери поне 1 храна за да видиш анализ"
              : "Pick at least 1 food to see analysis"}
          </div>
        ) : (
          <>
            <div className={`p-3 rounded-lg text-xs leading-relaxed ${
              avg >= 60
                ? (isDark ? "bg-emerald-500/15 text-emerald-100 border border-emerald-500/30" : "bg-emerald-50 text-emerald-900 border border-emerald-300")
                : avg >= 30
                ? (isDark ? "bg-amber-500/15 text-amber-100 border border-amber-500/30" : "bg-amber-50 text-amber-900 border border-amber-300")
                : (isDark ? "bg-red-500/15 text-red-100 border border-red-500/30" : "bg-red-50 text-red-900 border border-red-300")
            }`}>
              {avg >= 60
                ? (bg ? "Отличен профил — покрива ключови кофактори." : "Excellent profile — covers key cofactors.")
                : avg >= 30
                ? (bg ? "Приемлив, но има място за подобрение." : "Acceptable, room for improvement.")
                : (bg ? "Слабо COL3 покритие — виж препоръките." : "Weak COL3 coverage — see suggestions.")}
              {hasAntagonist && (
                <div className="mt-1 font-bold">
                  {bg ? "⚠ Съдържа AGE / LOX активатори (crosslinking риск)" : "⚠ Contains AGE / LOX activators (crosslinking risk)"}
                </div>
              )}
            </div>

            {/* Covered vs missing */}
            <div className="mt-3">
              <p className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                {bg ? "Кофактори" : "Cofactors"}
              </p>
              <div className="flex flex-wrap gap-1">
                {buckets.map(b => (
                  <span
                    key={b}
                    className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${
                      covered.has(b)
                        ? "bg-emerald-500/15 text-emerald-500 border-emerald-500/40"
                        : (isDark ? "bg-slate-500/15 text-slate-500 border-slate-500/30" : "bg-slate-100 text-slate-400 border-slate-300")
                    }`}
                  >
                    {covered.has(b) ? "✓" : "○"} {bucketLabel(b)}
                  </span>
                ))}
              </div>
            </div>

            {/* Suggestions */}
            {suggestions.length > 0 && (
              <div className="mt-3">
                <p className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  {bg ? "Препоръчани замествания" : "Recommended swaps"}
                </p>
                <div className="space-y-1.5">
                  {suggestions.map((s, i) => (
                    <div
                      key={i}
                      className={`flex items-center gap-2 p-2 rounded-lg text-[11px] ${
                        isDark ? "bg-purple-500/10 border border-purple-500/30" : "bg-purple-50 border border-purple-300"
                      }`}
                      data-testid={`col3-suggestion-${i}`}
                    >
                      <Plus className={`w-3 h-3 ${isDark ? "text-purple-300" : "text-purple-600"}`} />
                      <div className="flex-1">
                        <div className="font-bold">
                          {bg ? s.name_bg : s.name_en}
                          <span className={`ml-2 text-[9px] font-normal ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                            +{s.score} · {bg ? s.why_bg : s.why_en}
                          </span>
                        </div>
                        <div className={`text-[9px] ${isDark ? "text-purple-300/70" : "text-purple-700/70"}`}>
                          {bg ? `Ще покрие: ${bucketLabel(s.missing_bucket)}` : `Will cover: ${bucketLabel(s.missing_bucket)}`}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}


function FoodSlot({ idx, food, onPick, onClear, bg, isDark }) {
  const [query, setQuery] = useState("");
  const [openList, setOpenList] = useState(false);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return COL3_FOODS_BG.filter(f =>
      f.name_bg.toLowerCase().includes(q) ||
      f.name_en.toLowerCase().includes(q)
    ).slice(0, 6);
  }, [query]);

  if (food) {
    return (
      <div
        className={`flex items-center gap-2 p-3 rounded-lg border ${
          food.score >= 60
            ? (isDark ? "bg-emerald-500/10 border-emerald-500/30" : "bg-emerald-50 border-emerald-300")
            : food.score >= 30
            ? (isDark ? "bg-amber-500/10 border-amber-500/30" : "bg-amber-50 border-amber-300")
            : food.score === 0
            ? (isDark ? "bg-red-500/10 border-red-500/30" : "bg-red-50 border-red-300")
            : (isDark ? "bg-slate-500/10 border-slate-500/30" : "bg-slate-50 border-slate-300")
        }`}
        data-testid={`col3-slot-${idx}-filled`}
      >
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center font-black text-white ${
          food.score >= 60 ? "bg-emerald-500"
          : food.score >= 30 ? "bg-amber-500"
          : food.score === 0 ? "bg-red-500"
          : "bg-slate-500"
        }`}>
          {food.score}
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-bold truncate ${isDark ? "text-white" : "text-slate-900"}`}>
            {bg ? food.name_bg : food.name_en}
          </div>
          <div className={`text-[10px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            {bg ? food.why_bg : food.why_en}
          </div>
        </div>
        <button
          onClick={onClear}
          className={`h-7 w-7 rounded flex items-center justify-center ${isDark ? "hover:bg-white/10 text-slate-400" : "hover:bg-slate-100 text-slate-500"}`}
          data-testid={`col3-slot-${idx}-clear`}
          title={bg ? "Изчисти" : "Clear"}
        >
          <XIcon className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className={`flex items-center gap-2 p-2 rounded-lg border ${
        isDark ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
      }`}>
        <Search className={`w-4 h-4 flex-shrink-0 ${isDark ? "text-slate-500" : "text-slate-400"}`} />
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpenList(true); }}
          onFocus={() => setOpenList(true)}
          onBlur={() => setTimeout(() => setOpenList(false), 200)}
          placeholder={bg ? `Храна ${idx + 1} · напр. "Шипка", "Хляб"…` : `Food ${idx + 1} · e.g. "Rose-hip", "Bread"…`}
          data-testid={`col3-slot-${idx}-input`}
          className={`flex-1 bg-transparent text-xs outline-none ${isDark ? "text-white placeholder-slate-500" : "text-slate-900 placeholder-slate-400"}`}
        />
      </div>
      {openList && matches.length > 0 && (
        <div
          className={`absolute top-full mt-1 left-0 right-0 z-20 rounded-lg border shadow-xl overflow-hidden ${
            isDark ? "bg-slate-900 border-white/10" : "bg-white border-slate-200"
          }`}
          data-testid={`col3-slot-${idx}-dropdown`}
        >
          {matches.map(f => (
            <button
              key={f.key}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onPick(f); setQuery(""); setOpenList(false); }}
              className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 ${
                isDark ? "hover:bg-white/10 text-slate-200" : "hover:bg-slate-100 text-slate-800"
              }`}
              data-testid={`col3-slot-${idx}-match-${f.key}`}
            >
              <span className={`inline-block h-5 w-5 rounded flex items-center justify-center text-[10px] font-black text-white ${
                f.score >= 60 ? "bg-emerald-500"
                : f.score >= 30 ? "bg-amber-500"
                : f.score === 0 ? "bg-red-500"
                : "bg-slate-500"
              }`}>
                {f.score}
              </span>
              <span className="flex-1">{bg ? f.name_bg : f.name_en}</span>
              <span className={`text-[9px] ${isDark ? "text-slate-500" : "text-slate-400"}`}>
                {bg ? f.why_bg : f.why_en}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// SVG helpers
// ─────────────────────────────────────────────────────────────────────
function BreastTissueSVG({ isDark }) {
  const healthy = isDark ? "#22c55e" : "#16a34a";
  const dense   = isDark ? "#f59e0b" : "#d97706";
  const stroke  = isDark ? "#e2e8f0" : "#0f172a";
  return (
    <svg viewBox="0 0 260 160" className="w-full h-auto">
      {/* Left tissue (healthy) */}
      <g transform="translate(20, 20)">
        <rect x="0" y="0" width="100" height="120" rx="12" fill={healthy} fillOpacity="0.15" stroke={healthy} strokeOpacity="0.5" />
        <text x="50" y="14" fontSize="8" fontWeight="700" textAnchor="middle" fill={healthy}>COL3-rich</text>
        {/* Sparse, curly lines */}
        {[10, 25, 40, 55, 70, 85, 100].map((y, i) => (
          <path key={i} d={`M6 ${y + 15} Q30 ${y + 8} 50 ${y + 15} T94 ${y + 15}`} stroke={healthy} strokeWidth="1.2" fill="none" strokeOpacity="0.7" />
        ))}
        <circle cx="50" cy="70" r="8" fill={healthy} fillOpacity="0.3" stroke={healthy} strokeWidth="1" />
      </g>
      {/* Right tissue (dense) */}
      <g transform="translate(140, 20)">
        <rect x="0" y="0" width="100" height="120" rx="12" fill={dense} fillOpacity="0.15" stroke={dense} strokeOpacity="0.5" />
        <text x="50" y="14" fontSize="8" fontWeight="700" textAnchor="middle" fill={dense}>COL1-crosslinked</text>
        {/* Dense parallel lines (crosslinks) */}
        {[15, 22, 29, 36, 43, 50, 57, 64, 71, 78, 85, 92, 99, 106].map((y, i) => (
          <line key={i} x1="6" y1={y + 5} x2="94" y2={y + 5} stroke={dense} strokeWidth="1.4" strokeOpacity="0.8" />
        ))}
        <circle cx="50" cy="70" r="8" fill={dense} fillOpacity="0.35" stroke={dense} strokeWidth="1.5" />
      </g>
      {/* Arrow between */}
      <path d="M124 80 L136 80" stroke={stroke} strokeWidth="1.5" strokeOpacity="0.5" />
    </svg>
  );
}


function TripleHelixVisual({ builtCount, total, isDark }) {
  // Draw 3 sinusoidal strands. Each strand becomes "active" (colored) as
  // more cofactors are collected: 0-1 = 0 strands, 2-3 = 1, 4 = 2, 5 = 3.
  const activeStrands = builtCount === 0 ? 0
                      : builtCount <= 1 ? 1
                      : builtCount <= 2 ? 1
                      : builtCount <= 3 ? 2
                      : builtCount <= 4 ? 2
                      : 3;
  const stroke = isDark ? "#334155" : "#e2e8f0";
  const strandColors = ["#a855f7", "#ec4899", "#f59e0b"];

  const path = (offset) => {
    let d = `M10 ${20 + offset}`;
    for (let x = 20; x <= 160; x += 10) {
      d += ` Q${x - 5} ${20 + offset + Math.sin((x + offset * 6) * 0.13) * 14}, ${x} ${20 + offset}`;
    }
    return d;
  };

  return (
    <div className="relative">
      <svg viewBox="0 0 170 100" className="w-full h-auto">
        {[0, 15, 30].map((offset, i) => (
          <path
            key={i}
            d={path(offset)}
            stroke={i < activeStrands ? strandColors[i] : stroke}
            strokeWidth={i < activeStrands ? 3 : 1.6}
            fill="none"
            style={{ transition: "stroke 0.6s ease, stroke-width 0.6s ease" }}
          />
        ))}
      </svg>
      <div className={`text-center text-[10px] font-bold uppercase tracking-widest mt-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
        {builtCount === total
          ? "Triple helix stable"
          : `Strand ${activeStrands}/3`}
      </div>
    </div>
  );
}


function BalanceScale({ tilt, type1Pct, type3Pct, inHealthyZone, isDark, bg }) {
  const beamStroke = isDark ? "#cbd5e1" : "#475569";
  return (
    <div className="flex flex-col items-center">
      <div
        className="relative h-40 w-full max-w-md mx-auto"
        style={{ perspective: "800px" }}
      >
        <svg viewBox="0 0 400 160" className="w-full h-full">
          {/* Fulcrum */}
          <polygon points="200,150 175,150 195,120" fill={beamStroke} />
          <polygon points="200,150 225,150 205,120" fill={beamStroke} />
          <circle cx="200" cy="120" r="4" fill={beamStroke} />
          {/* Beam (tilts) */}
          <g style={{ transform: `rotate(${tilt}deg)`, transformOrigin: "200px 120px", transition: "transform 0.4s ease" }}>
            <line x1="60" y1="120" x2="340" y2="120" stroke={beamStroke} strokeWidth="4" strokeLinecap="round" />
            {/* Left pan (COL1) */}
            <line x1="80" y1="120" x2="80" y2="70" stroke={beamStroke} strokeOpacity="0.5" strokeWidth="1.5" />
            <ellipse cx="80" cy="70" rx="42" ry="10" fill={isDark ? "#f59e0b" : "#f59e0b"} fillOpacity="0.9" />
            <text x="80" y="60" fontSize="12" fontWeight="800" textAnchor="middle" fill={isDark ? "#fef3c7" : "#78350f"}>
              COL1 {type1Pct}%
            </text>
            {/* Right pan (COL3) */}
            <line x1="320" y1="120" x2="320" y2="70" stroke={beamStroke} strokeOpacity="0.5" strokeWidth="1.5" />
            <ellipse cx="320" cy="70" rx="42" ry="10" fill="#a855f7" fillOpacity="0.9" />
            <text x="320" y="60" fontSize="12" fontWeight="800" textAnchor="middle" fill={isDark ? "#f3e8ff" : "#581c87"}>
              COL3 {type3Pct}%
            </text>
          </g>
        </svg>
      </div>
      <div className={`mt-1 text-[11px] font-bold ${inHealthyZone
        ? (isDark ? "text-emerald-300" : "text-emerald-600")
        : (isDark ? "text-slate-400" : "text-slate-500")}`}>
        {inHealthyZone
          ? (bg ? "БАЛАНСИРАН СИНТЕЗ" : "BALANCED SYNTHESIS")
          : (bg ? "Извън здрава зона" : "Off healthy zone")}
      </div>
    </div>
  );
}


// Tiny confetti burst (no library) — just a handful of animated dots.
function ConfettiBurst() {
  const dots = Array.from({ length: 24 });
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {dots.map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 0.3;
        const color = ["#a855f7", "#ec4899", "#f59e0b", "#22c55e", "#38bdf8"][i % 5];
        return (
          <span
            key={i}
            className="absolute top-0 h-2 w-2 rounded-sm confetti-burst"
            style={{
              left: `${left}%`,
              backgroundColor: color,
              animationDelay: `${delay}s`,
            }}
          />
        );
      })}
      <style>{`
        @keyframes col3-confetti-fall {
          0%   { transform: translate3d(0, -10px, 0) rotate(0);   opacity: 1; }
          100% { transform: translate3d(${Math.random() * 40 - 20}px, 400px, 0) rotate(720deg); opacity: 0; }
        }
        .confetti-burst {
          animation: col3-confetti-fall 1.8s cubic-bezier(0.2, 0.7, 0.4, 1) forwards;
        }
      `}</style>
    </div>
  );
}
